import { Badge } from './ui/Badge';
import { Fact } from './ui/Facts';
import { formatDateTime } from '../lib/format';
import type { Ec2Server } from '../lib/api';

/**
 * What EC2 says about an X5 server, for any page that has resolved one from a
 * stored address. Deliberately does *not* render the address the row stored —
 * that differs per caller (the SMS registry keeps a bare IP, an X5 Application
 * keeps a URL) and belongs next to the rest of that record's own fields.
 *
 * Tags are listed wholesale rather than curated: `TeleportHostname` is how you
 * reach the box and `DataExportName` is its datalake client id, and a tag added
 * later should appear here without a code change.
 */
export function ServerFacts({ server }: { server: Ec2Server }) {
  const stopped = server.state !== 'running';
  return (
    <>
      <Fact
        label="State"
        value={
          <span className="flex flex-wrap items-center justify-end gap-1.5">
            <Badge variant={stopped ? 'warning' : 'success'}>{server.state}</Badge>
            {stopped && server.stateTransitionReason && (
              <span className="text-xs text-muted-foreground">{server.stateTransitionReason}</span>
            )}
          </span>
        }
      />
      <Fact label="Instance id" value={server.instanceId} mono />
      <Fact label="Instance type" value={server.instanceType} />
      <Fact label="Availability zone" value={server.availabilityZone} />
      <Fact label="Private IP" value={server.privateIp} mono />
      <Fact label="Private DNS" value={server.privateDnsName} mono />
      <Fact label="Launched" value={formatDateTime(server.launchTime)} />
      {server.tags.map((t) => (
        <Fact key={t.key} label={t.key} value={t.value || '—'} mono />
      ))}
    </>
  );
}

/**
 * The instance name with its address underneath — the shape both estate tables
 * use in place of a bare address. Rendered as a button because opening the
 * detail drawer is the point of showing it.
 */
export function ServerCell({
  server,
  address,
  onOpen,
}: {
  server: Ec2Server | null;
  address: string;
  onOpen: () => void;
}) {
  const stopped = server !== null && server.state !== 'running';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block cursor-pointer text-left"
      title="Show instance detail"
    >
      <div className="flex items-center gap-1.5">
        {server?.name ? (
          <span className="text-fg-2 group-hover:text-accent group-hover:underline">
            {server.name}
          </span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground italic group-hover:text-accent">
            no instance
          </span>
        )}
        {stopped && <Badge variant="warning">{server.state}</Badge>}
      </div>
      <div className="font-mono text-xs text-muted-foreground/70">
        {address}
        {server && ` \u00b7 ${server.instanceId}`}
      </div>
    </button>
  );
}
